/* Explainability — the differentiating feature (UI-UX-DESIGN.md §6, SPEC-01 §10).
 *
 * Command PE's manual has a section titled "My @#%^@#% weapon won't fire!!!". This
 * panel is the answer to it: the check chain, the failing condition, the actual
 * values, and the two actions that would resolve it.
 *
 * It is also a V&V surface — `model` records WHICH model produced the result, so a
 * run using a customer plugin is distinguishable from one using the built-in default.
 */
import { AlertTriangle, ArrowRight, Check, CircleSlash, Cpu, Minus, X } from 'lucide-react'
import type { DecisionTrace, TraceCheck } from '../data/types'
import { useDeck } from '../state/store'
import { fetchTraces, toDecisionTrace } from '../lib/liveTraces'
import { useEffect, useState } from 'react'
import type { DecisionTrace as DecisionTraceType } from '../data/types'

function CheckIcon({ result }: { result: TraceCheck['result'] }) {
  if (result === 'pass')
    return <Check size={13} style={{ color: 'var(--color-ok)' }} aria-label="Pass" />
  if (result === 'fail')
    return <X size={13} style={{ color: 'var(--color-crit)' }} aria-label="Fail" />
  return <Minus size={13} style={{ color: 'var(--color-text-3)' }} aria-label="Not evaluated" />
}

function ChainRow({ check }: { check: TraceCheck }) {
  const failed = check.result === 'fail'
  const skipped = check.result === 'not_evaluated'

  return (
    <div
      className="flex gap-2 px-3 py-1.5"
      style={{
        borderBottom: '1px solid var(--color-line-1)',
        background: failed ? 'rgba(232,81,90,0.07)' : undefined,
      }}
    >
      <div className="mt-px shrink-0">
        <CheckIcon result={check.result} />
      </div>
      <div className="min-w-0 flex-1">
        <div
          style={{
            fontSize: 12,
            fontWeight: failed ? 500 : 400,
            color: skipped ? 'var(--color-text-3)' : 'var(--color-text-1)',
          }}
        >
          {check.label}
        </div>
        {check.values && (
          <dl className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {Object.entries(check.values).map(([k, v]) => (
              <div key={k} className="flex items-baseline gap-1">
                <dt className="label" style={{ fontSize: 9 }}>
                  {k}
                </dt>
                <dd
                  className="num"
                  style={{
                    fontSize: 11,
                    color: failed ? 'var(--color-crit)' : 'var(--color-text-2)',
                  }}
                >
                  {v}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  )
}

function TraceCard({ trace }: { trace: DecisionTrace }) {
  const declined = trace.outcome === 'declined'

  return (
    <article
      className="mb-2 overflow-hidden rounded"
      style={{
        background: 'var(--color-surface-2)',
        border: `1px solid ${declined ? 'rgba(232,81,90,0.32)' : 'var(--color-line-2)'}`,
      }}
    >
      {/* Headline */}
      <div
        className="flex items-start gap-2 px-3 py-2"
        style={{
          background: declined ? 'rgba(232,81,90,0.10)' : 'rgba(79,196,138,0.07)',
          borderBottom: '1px solid var(--color-line-1)',
        }}
      >
        {declined ? (
          <AlertTriangle size={14} style={{ color: 'var(--color-crit)', marginTop: 1 }} aria-hidden />
        ) : (
          <Check size={14} style={{ color: 'var(--color-ok)', marginTop: 1 }} aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--color-text-1)' }}>
            {trace.headline}
          </div>
          <div className="mt-px flex items-center gap-2">
            <span className="num" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
              {trace.simTime}
            </span>
            <span style={{ fontSize: 10, color: 'var(--color-text-3)' }}>·</span>
            <span className="num" style={{ fontSize: 10, color: 'var(--color-text-2)' }}>
              {trace.subject}
            </span>
          </div>
        </div>
      </div>

      {/* The check chain */}
      <div>
        {trace.chain.map((c) => (
          <ChainRow key={c.check} check={c} />
        ))}
      </div>

      {/* Remedies — a refusal without a resolution path is a dead end */}
      {trace.remedies && trace.remedies.length > 0 && (
        <div className="flex flex-col gap-1 px-3 py-2">
          {trace.remedies.map((r) => (
            <button
              key={r.label}
              className="flex items-center justify-between rounded px-2 py-1 text-left transition-colors"
              style={{
                background: 'var(--color-surface-3)',
                fontSize: 11.5,
                color: 'var(--color-text-1)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-surface-4)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--color-surface-3)')}
            >
              <span>{r.label}</span>
              <ArrowRight size={12} style={{ color: 'var(--color-accent-hi)' }} aria-hidden />
            </button>
          ))}
        </div>
      )}

      {/* Model provenance — the V&V hook */}
      <div
        className="flex items-center gap-1.5 px-3 py-1.5"
        style={{ borderTop: '1px solid var(--color-line-1)', background: 'var(--color-surface-1)' }}
      >
        <Cpu size={11} style={{ color: 'var(--color-text-3)' }} aria-hidden />
        <span className="label" style={{ fontSize: 9 }}>
          {trace.model.stage}
        </span>
        <span className="num" style={{ fontSize: 10, color: 'var(--color-text-2)' }}>
          {trace.model.pluginId ?? trace.model.implementation}
        </span>
        {trace.model.pluginVersion && (
          <span className="num" style={{ fontSize: 9, color: 'var(--color-text-3)' }}>
            v{trace.model.pluginVersion}
          </span>
        )}
      </div>
    </article>
  )
}

export function TracePanel({ entityId }: { entityId: string | null }) {
  const live = useDeck((s) => s.connection === 'live')
  const epoch = useDeck((s) => s.epochIso)
  const [engineTraces, setEngineTraces] = useState<DecisionTraceType[]>([])

  // Fetched on selection change and then polled slowly, NOT carried on the snapshot stream.
  // Traces are large and only the selected unit's are wanted; putting them in a 5 Hz broadcast
  // would multiply the stream by the entity count to show something nobody is looking at.
  //
  // Every ~2 s rather than every tick: a trace that rewrites itself five times a second is
  // unreadable, and the operator is studying it, not watching it.
  useEffect(() => {
    if (!live || !entityId) {
      setEngineTraces([])
      return
    }
    let cancelled = false
    const load = async () => {
      const raw = await fetchTraces(entityId)
      if (!cancelled) setEngineTraces(raw.map((t, i) => toDecisionTrace(t, epoch, i)))
    }
    void load()
    const id = window.setInterval(load, 2000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
    // `tick` is deliberately absent: including it would refetch on every snapshot and defeat the
    // point of polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, entityId, epoch])

  // There is no fixture fallback. Offline shows the empty state, which is the honest answer: a
  // panel that displays a plausible scripted refusal while disconnected is indistinguishable from
  // one showing a real engine decision, and that confusion is the exact thing this panel exists
  // to eliminate.
  const list = live ? engineTraces : []

  if (list.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
        <CircleSlash size={20} style={{ color: 'var(--color-text-3)' }} aria-hidden />
        <p style={{ fontSize: 12, color: 'var(--color-text-2)' }}>
          No decisions recorded for this unit yet.
        </p>
        <p style={{ fontSize: 11, color: 'var(--color-text-3)' }}>
          Every engagement, detection, and refusal is traced. Traces appear here as the
          simulation resolves them.
        </p>
      </div>
    )
  }

  return (
    <div className="p-2">
      <div className="mb-2 flex items-center justify-between px-1">
        <span className="label">Decision trace</span>
        <span className="num" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
          {list.length} recent
        </span>
      </div>
      {list.map((t) => (
        <TraceCard key={t.id} trace={t} />
      ))}
    </div>
  )
}
